import React, { useState, useCallback, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { LockOpenOutlined, LockOutlined, BusinessOutlined, MailOutlineOutlined } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";

import { useAuthContext } from "../../../../../context/auth.context";
import Button from "../../../../components/Button/Button";
import TextInput from "../../../../components/Inputs/TextInput/TextInput";
import PageLoader from "../../../../components/PageLoader/PageLoader";
import IconButton from "../../../../components/IconButton/IconButton";
import { emailRules, passwordRules } from "./form-validation-rules";
import { SsoDiscoverResponse, SsoInitiateResponse, SsoProvider } from "../../../../../models/sso.model";
import usePersistedState from "../../../../../hooks/persisted-state.hook";
import environment from "../../../../../env";
import { describeSsoInitiateError, isAccountNotFoundError } from "../../../../../shared/utils/auth-error";

// Types

type LoginStep =
  | { kind: "email" }
  | {
    kind: "discovered";
    email: string;
    // The email exactly as the user typed it (trimmed only) -- kept alongside the lowercased
    // `email` above so the password-login call can send the backend the real casing for its
    // historical-mixed-case-account fallback. `email` itself stays lowercase because it also
    // drives SSO discover/initiate/exchange and the UI chip, which are correctly DB-driven and
    // don't need it.
    typedEmail: string;
    enforceSSO: boolean;
    ssoExcluded: boolean;
    providers: SsoProvider[];
  }
  // Cognito answered the sign in with NEW_PASSWORD_REQUIRED -- the account's password was reset
  // by a Super Admin, so a new one has to be chosen here before any session is issued.
  | {
    kind: "newPassword";
    email: string;
    session: string;
  };

// Styles

const useStyles = makeStyles((theme) => ({
  emailChip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 56,
    boxSizing: "border-box",
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.75),
  },
  emailInfo: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(1),
  },
  emailIcon: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: 20,
  },
  emailLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 500,
  },
  changeLink: {
    flexShrink: 0,
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },
  ssoHint: {
    textAlign: "center",
  },
  orDivider: {
    display: "flex",
    alignItems: "center",
  },
  orLine: {
    flex: 1,
  },
  orText: {
    padding: theme.spacing(0, 1),
  },
  errorDetails: {
    display: "block",
    marginTop: theme.spacing(0.5),
    opacity: 0.85,
    wordBreak: "break-word",
  },
}));

// CSRF helpers

function generateCsrf(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Component

function LoginForm({ onSsoOnlyChange }: { onSsoOnlyChange?: (ssoOnly: boolean) => void }) {
  const classes = useStyles();
  const { login, completeNewPassword, authError, clearAuthError } = useAuthContext();
  const history = useHistory();
  const location = useLocation<{ from?: { pathname: string; search: string; hash: string } }>();

  const [step, setStep] = useState<LoginStep>({ kind: "email" });

  // SSO-only accounts (no password provision) have their "Forgot password" / "Sign up"
  // links hidden on this step-2 screen only; step 1 (email entry) is left untouched. The
  // forced-new-password step hides them too -- the user is mid-challenge and those links would
  // only lead them away from finishing it.
  useEffect(() => {
    onSsoOnlyChange?.(step.kind === "newPassword" || (step.kind === "discovered" && step.enforceSSO));
  }, [step, onSsoOnlyChange]);
  const [discovering, setDiscovering] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [newPasswordLoading, setNewPasswordLoading] = useState(false);
  const [ssoLoadingProvider, setSsoLoadingProvider] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Failures raised outside this form -- an SSO callback that came back rejected, a session that
  // died in the background -- are reported by AuthContext and shown in the same banner. A local
  // error always wins, since it belongs to whatever the user just did.
  const banner = error ? { message: error, details: undefined as string | undefined } : authError;

  // The failed attempt already told us who was signing in, so don't make them retype it.
  const [initialEmail] = useState(() => authError?.email ?? "");

  // "Stay logged in": persists the session via a cookie shared across tabs (cleared when the
  // browser closes) instead of the default tab-scoped session. Chosen on step 1 so it applies
  // regardless of whether the user ends up signing in with a password or via SSO. The checkbox
  // itself defaults to the user's last choice (localStorage) -- this is just a UI preference,
  // not auth data, so it's fine to persist indefinitely unlike the session itself.
  const [remember, setRemember] = usePersistedState<boolean>("login/rememberMe", {
    storageType: "local",
    defaultValue: false,
  });

  const emailForm = useForm<{ email: string }>({ mode: "onBlur" });
  const passwordForm = useForm<{ password: string }>({ mode: "onBlur" });
  const newPasswordForm = useForm<{ newPassword: string; confirmPassword: string }>({ mode: "onBlur" });

  // Step 1: Email discovery

  const handleDiscovery = useCallback(async ({ email: rawEmail }: { email: string }) => {
    // Cognito/the backend treat the email case-insensitively and trim it before every lookup --
    // normalize here too so the value shown in the "Change" chip and carried through SSO
    // (initiate/exchange) matches what was actually looked up, not stray whitespace/casing. The
    // password-login step below uses the separately-kept typedEmail instead, so a historical
    // mixed-case Cognito account can still be found.
    const typedEmail = rawEmail.trim();
    const email = typedEmail.toLowerCase();
    setError("");
    clearAuthError();
    setDiscovering(true);
    try {
      const { data } = await axios.post<SsoDiscoverResponse>(
        "auth/sso/discover",
        { email },
        { baseURL: environment.newApiUrl }
      );
      if (data.notFound) {
        // Stay on step 1 (email entry) -- no point moving to a password/SSO step for an
        // account that doesn't exist.
        setError("No account found for this email.");
        return;
      }
      setStep({
        kind: "discovered",
        email,
        typedEmail,
        enforceSSO: data.enforceSSO,
        ssoExcluded: data.ssoExcluded ?? false,
        providers: data.providers ?? [],
      });
    } catch {
      setError("Unable to verify your account. Please try again.");
    } finally {
      setDiscovering(false);
    }
  }, [clearAuthError]);

  // Step 2a: Password login

  const handlePasswordLogin = useCallback(
    async ({ password }: { password: string }) => {
      if (step.kind !== "discovered") return;
      setError("");
      clearAuthError();
      setPasswordLoading(true);
      try {
        // typedEmail (not the lowercased step.email) so the backend can fall back to a
        // historical mixed-case Cognito account when no lowercase account exists yet.
        const result = await login({ username: step.typedEmail, password, remember });
        if (result?.challenge === "NEW_PASSWORD_REQUIRED") {
          // Signed in with a password that is only good once (a Super Admin reset it) -- collect
          // a real one before going anywhere.
          setStep({ kind: "newPassword", email: result.username, session: result.session });
          setPasswordLoading(false);
          return;
        }
        const from = location.state?.from;
        const destination =
          from && from.pathname?.startsWith("/") && !from.pathname.startsWith("//") ? from : "/";
        history.replace(destination);
      } catch (err: any) {
        if (isAccountNotFoundError(err)) {
          // Neither the lowercase nor the historical exact-typed-case Cognito account exists --
          // send them to register instead of showing a dead-end "invalid credentials" error.
          history.push({ pathname: "/register", state: { message: err.response.data.message } });
          return;
        }
        const code = err?.response?.data?.code;
        if (code === "SSO_REQUIRED") {
          setError("This account requires Single Sign-On. Please use your organisation login.");
        } else {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Sign in failed. Please check your credentials.";
          setError(msg);
        }
        setPasswordLoading(false);
      }
    },
    [step, login, history, location.state, remember, clearAuthError]
  );

  // Step 3: Forced new password (NEW_PASSWORD_REQUIRED)

  const handleSetNewPassword = useCallback(
    async ({ newPassword }: { newPassword: string; confirmPassword: string }) => {
      if (step.kind !== "newPassword") return;
      setError("");
      clearAuthError();
      setNewPasswordLoading(true);
      try {
        await completeNewPassword({ username: step.email, session: step.session, newPassword, remember });
        const from = location.state?.from;
        const destination =
          from && from.pathname?.startsWith("/") && !from.pathname.startsWith("//") ? from : "/";
        history.replace(destination);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Could not set your new password. Please try again.";
        setError(msg);
        setNewPasswordLoading(false);
      }
    },
    [step, completeNewPassword, history, location.state, remember, clearAuthError]
  );

  // Step 2b: SSO login

  const handleSsoLogin = useCallback(
    async (providerName: string, email: string) => {
      setError("");
      clearAuthError();
      setSsoLoadingProvider(providerName);
      try {
        const csrf = generateCsrf();
        const state = btoa(JSON.stringify({ csrf, providerName, email, remember }));
        sessionStorage.setItem("sso_csrf", csrf);
        const { data } = await axios.post<SsoInitiateResponse>(
          "auth/sso/initiate",
          { providerName, state },
          { baseURL: environment.newApiUrl }
        );
        if (!data?.redirectUrl) {
          throw new Error("Missing redirect URL in SSO initiate response.");
        }
        window.location.href = data.redirectUrl;
      } catch (err: any) {
        // Never leave a dangling CSRF token behind for a hand-off that never happened.
        sessionStorage.removeItem("sso_csrf");
        setSsoLoadingProvider(null);
        setError(describeSsoInitiateError(err).message);
      }
    },
    [remember, clearAuthError]
  );

  // Navigation

  const handleChangeEmail = useCallback(() => {
    setStep({ kind: "email" });
    setError("");
    clearAuthError();
    passwordForm.reset();
    newPasswordForm.reset();
  }, [passwordForm, newPasswordForm, clearAuthError]);

  const isAnyLoading = discovering || passwordLoading || newPasswordLoading || ssoLoadingProvider !== null;

  // Render: SSO buttons
  // SSO-only mode uses contained (primary action); excluded mode uses outlined (secondary)

  const renderSsoButtons = (providers: SsoProvider[], variant: "contained" | "outlined", email: string) =>
    providers.map((provider) => {
      const isLoading = ssoLoadingProvider === provider.providerName;
      const label = provider.displayName || provider.providerName;
      return (
        <Grid item xs={12} key={provider.providerName}>
          <Button
            fullWidth
            variant={variant}
            color="primary"
            disabled={isAnyLoading}
            onClick={() => handleSsoLogin(provider.providerName, email)}
            startIcon={
              isLoading ? <CircularProgress size={18} color="inherit" /> : <BusinessOutlined />
            }
          >
            {isLoading ? "Connecting..." : `Continue with ${provider.providerName} ${provider.displayName ? `(${provider.displayName})` : ""}`}
          </Button>
        </Grid>
      );
    });

  // Render

  return (
    <>
      {/* STEP 1 -- Email */}
      {step.kind === "email" && (
        <form onSubmit={emailForm.handleSubmit(handleDiscovery)} noValidate>
          <Grid container spacing={2}>
            {banner && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {banner.message}
                  {banner.details && (
                    <Typography variant="caption" component="span" className={classes.errorDetails}>
                      {banner.details}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                name="email"
                control={emailForm.control}
                defaultValue={initialEmail}
                rules={emailRules()}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextInput
                    inputRef={ref}
                    {...rest}
                    margin="none"
                    fullWidth
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    label={fieldState.error?.message || "Email"}
                    error={Boolean(fieldState.error)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="textPrimary">
                    Stay logged in on this browser
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" fullWidth variant="contained" color="primary" disabled={discovering}>
                {discovering ? <CircularProgress size={20} color="inherit" /> : "Continue"}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}

      {/* STEP 2 -- After discovery */}
      {step.kind === "discovered" && (
        <Grid container spacing={2}>
          {/* Email + Change */}
          <Grid item xs={12}>
            <Box className={classes.emailChip}>
              <Box className={classes.emailInfo}>
                <MailOutlineOutlined className={classes.emailIcon} />
                <Typography variant="body2" color="textPrimary" className={classes.emailLabel}>
                  {step.email}
                </Typography>
              </Box>
              <Link
                component="button"
                type="button"
                variant="body2"
                color="primary"
                className={classes.changeLink}
                onClick={handleChangeEmail}
              >
                Change
              </Link>
            </Box>
          </Grid>

          {/* Error banner */}
          {banner && (
            <Grid item xs={12}>
              <Alert severity="error">
                {banner.message}
                {banner.details && (
                  <Typography variant="caption" component="span" className={classes.errorDetails}>
                    {banner.details}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}

          {/* ---- SSO enforced: SSO buttons only ---- */}
          {step.enforceSSO && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" className={classes.ssoHint}>
                  This account uses Single Sign-On
                </Typography>
              </Grid>
              {renderSsoButtons(step.providers, "contained", step.email)}
            </>
          )}

          {/* ---- Password form ---- */}
          {!step.enforceSSO && (
            <Grid item xs={12}>
              <form onSubmit={passwordForm.handleSubmit(handlePasswordLogin)} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="password"
                      control={passwordForm.control}
                      defaultValue=""
                      rules={{ required: { value: true, message: "Password is required" } }}
                      render={({ field: { ref, ...rest }, fieldState }) => (
                        <TextInput
                          inputRef={ref}
                          {...rest}
                          margin="none"
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          id="password"
                          autoComplete="current-password"
                          autoFocus
                          label={fieldState.error?.message || "Password"}
                          error={Boolean(fieldState.error)}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                type="button"
                                edge="right"
                                onClick={() => setShowPassword((v) => !v)}
                              >
                                {showPassword ? <LockOpenOutlined /> : <LockOutlined />}
                              </IconButton>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={passwordLoading || ssoLoadingProvider !== null}
                    >
                      {passwordLoading ? <CircularProgress size={20} color="inherit" /> : "Sign in"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}

          {/* ---- SSO excluded: "or" divider + outlined SSO buttons ---- */}
          {!step.enforceSSO && step.providers.length > 0 && (
            <>
              <Grid item xs={12}>
                <Box className={classes.orDivider}>
                  <Divider className={classes.orLine} />
                  <Typography variant="caption" color="textSecondary" className={classes.orText}>
                    or
                  </Typography>
                  <Divider className={classes.orLine} />
                </Box>
              </Grid>
              {renderSsoButtons(step.providers, "outlined", step.email)}
            </>
          )}
        </Grid>
      )}

      {/* STEP 3 -- Forced new password */}
      {step.kind === "newPassword" && (
        <form onSubmit={newPasswordForm.handleSubmit(handleSetNewPassword)} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.emailChip}>
                <Box className={classes.emailInfo}>
                  <MailOutlineOutlined className={classes.emailIcon} />
                  <Typography variant="body2" color="textPrimary" className={classes.emailLabel}>
                    {step.email}
                  </Typography>
                </Box>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  color="primary"
                  className={classes.changeLink}
                  onClick={handleChangeEmail}
                >
                  Change
                </Link>
              </Box>
            </Grid>

            {banner && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {banner.message}
                  {banner.details && (
                    <Typography variant="caption" component="span" className={classes.errorDetails}>
                      {banner.details}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" className={classes.ssoHint}>
                Your password was reset by an administrator. Choose a new one to finish signing in.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="newPassword"
                control={newPasswordForm.control}
                defaultValue=""
                rules={passwordRules("New password")}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextInput
                    inputRef={ref}
                    {...rest}
                    margin="none"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    autoComplete="new-password"
                    autoFocus
                    label={fieldState.error?.message || "New password"}
                    error={Boolean(fieldState.error)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          type="button"
                          edge="right"
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? <LockOpenOutlined /> : <LockOutlined />}
                        </IconButton>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="confirmPassword"
                control={newPasswordForm.control}
                defaultValue=""
                rules={{
                  required: { value: true, message: "Please confirm your new password" },
                  validate: {
                    matches: (value: string) =>
                      value === newPasswordForm.getValues("newPassword") ? true : "Passwords do not match",
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextInput
                    inputRef={ref}
                    {...rest}
                    margin="none"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    autoComplete="new-password"
                    label={fieldState.error?.message || "Confirm new password"}
                    error={Boolean(fieldState.error)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" fullWidth variant="contained" color="primary" disabled={newPasswordLoading}>
                {newPasswordLoading ? <CircularProgress size={20} color="inherit" /> : "Set password and sign in"}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}

      {(passwordLoading || discovering || newPasswordLoading) && <PageLoader opacity="0.5" position="fixed" />}
    </>
  );
}

export default LoginForm;
